import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { GmailAuditLogsTable } from "@/components/GmailAuditLogsTable";
import { formatDateTime } from "@/lib/format";

type GmailConnection = components["schemas"]["GmailConnectionAdminResponse"];
type GmailConnectionStatus = components["schemas"]["GmailConnectionStatus"];

function statusVariant(status: GmailConnectionStatus): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Connected":
      return "default";
    case "Backfilling":
      return "secondary";
    case "NeedsReauth":
    case "Revoked":
      return "destructive";
    case "Pending":
    default:
      return "outline";
  }
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

export function GmailConnectionPage() {
  const { gmailConnectionId } = useParams<{ gmailConnectionId: string }>();

  const [connection, setConnection] = useState<GmailConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gmailConnectionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchConnection() {
      setLoading(true);
      const { data, error: apiError } = await api.GET("/admin/gmail-connections/{id}", {
        params: { path: { id: gmailConnectionId! } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load Gmail connection.");
      } else {
        setConnection(data ?? null);
      }
      setLoading(false);
    }

    fetchConnection();
    return () => {
      cancelled = true;
    };
  }, [gmailConnectionId]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Gmail connection</h1>
        {connection && (
          <Button asChild variant="outline">
            <a href={connection.datadogTracesUrl} target="_blank" rel="noreferrer">
              View traces in Datadog
            </a>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : connection ? (
        <>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{connection.emailAddress}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="Connection">
                <Field label="Connection ID">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {connection.id}
                  </Badge>
                </Field>
                <Field label="Organisation">
                  <Link to={`/organisations/${connection.organisationId}`}>
                    <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                      {connection.organisationId}
                    </Badge>
                  </Link>
                </Field>
                <Field label="Status">
                  <Badge variant={statusVariant(connection.status)}>{connection.status}</Badge>
                </Field>
                <Field label="Email">{connection.emailAddress}</Field>
                <Field label="Google subject" className="sm:col-span-2">
                  <span className="font-mono text-xs">{connection.googleSubject}</span>
                </Field>
              </Section>

              <Section title="Sync state">
                <Field label="Last synced at">{formatDateTime(connection.lastSyncedAt)}</Field>
                <Field label="Time since last sync">
                  {formatDuration(
                    connection.secondsSinceLastSync == null
                      ? null
                      : Number(connection.secondsSinceLastSync),
                  )}
                </Field>
                <Field label="Last history ID">
                  <span className="font-mono text-xs">{connection.lastHistoryId ?? "—"}</span>
                </Field>
              </Section>

              <Section title="Access token">
                <Field label="Expires at">{formatDateTime(connection.accessTokenExpiresAt)}</Field>
                <Field label="Expired now">
                  <Badge variant={connection.accessTokenExpired ? "destructive" : "default"}>
                    {connection.accessTokenExpired ? "Yes" : "No"}
                  </Badge>
                </Field>
                <Field label="Has refresh token">
                  <Badge variant={connection.hasRefreshToken ? "default" : "destructive"}>
                    {connection.hasRefreshToken ? "Yes" : "No"}
                  </Badge>
                </Field>
                <Field label="Refresh token secret ref" className="sm:col-span-3">
                  <span className="font-mono text-xs">
                    {connection.refreshTokenSecretRef ?? "—"}
                  </span>
                </Field>
              </Section>

              <Section title="Watch">
                <Field label="Expires at">{formatDateTime(connection.watchExpiresAt)}</Field>
                <Field label="Expired now">
                  <Badge variant={connection.watchExpired ? "destructive" : "default"}>
                    {connection.watchExpired ? "Yes" : "No"}
                  </Badge>
                </Field>
              </Section>

              <Section title="Backfill">
                <Field label="Backfilled since">{formatDateTime(connection.backfilledSince)}</Field>
                <Field label="Backfilled until">{formatDateTime(connection.backfilledUntil)}</Field>
              </Section>

              <Section title="Attachments">
                <Field label="Candidate">{connection.attachmentCounts.candidate}</Field>
                <Field label="Imported">{connection.attachmentCounts.imported}</Field>
                <Field label="Filtered">{connection.attachmentCounts.filtered}</Field>
                <Field label="Failed">
                  <span className={Number(connection.attachmentCounts.failed) > 0 ? "text-destructive font-semibold" : ""}>
                    {connection.attachmentCounts.failed}
                  </span>
                </Field>
                <Field label="Dismissed">{connection.attachmentCounts.dismissed}</Field>
                <Field label="Failed (last 24h)">
                  <span className={Number(connection.attachmentCounts.failedLast24h) > 0 ? "text-destructive font-semibold" : ""}>
                    {connection.attachmentCounts.failedLast24h}
                  </span>
                </Field>
              </Section>

              <Section title="Audit">
                <Field label="Created">{formatDateTime(connection.createdAt)}</Field>
                <Field label="Created by">
                  <ActorLink actorId={connection.createdBy} />
                </Field>
                <Field label="Updated">{formatDateTime(connection.updatedAt)}</Field>
                <Field label="Updated by">
                  <ActorLink actorId={connection.updatedBy} />
                </Field>
              </Section>
            </CardContent>
          </Card>

          <h2 className="mb-4 mt-8 text-lg font-semibold">Audit log</h2>
          <GmailAuditLogsTable
            organisationId={connection.organisationId}
            gmailConnectionId={connection.id}
          />
        </>
      ) : (
        <p className="text-muted-foreground">Gmail connection not found.</p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
